import React, { useCallback, useMemo } from "react";
import classnames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { selectors, actions } from "@shared/store";
import { MODAL_TYPES, OVERLAY_TYPES } from "@shared/constants";
import { UserCreateEditContainer } from "@containers/User/containers/UserCreateEditContainer";
import { StationCreateEditContainer } from "@containers/Station/containers/StationCreateEditContainer";
import { SensorCreateEditContainer } from "@containers/Sensor/containers/SensorCreateEditContainer";
import { PatientCreateEditContainer } from "@containers/Patient/containers/PatientCreateEditContainer";
import { SensorDataFileCreateContainer } from "@containers/SensorDataFile/containers/SensorDataFileCreateContainer";
import { SensorDataSessionCreateContainer } from "@containers/SensorDataSession/containers/SensorDataSessionCreateContainer";
import { AlertCreateEditContainer } from "@containers/Alert/containers/AlertCreateEditContainer";
import { AlertNotificationsListContainer } from "@containers/Alert/containers/AlertNotificationsListContainer";
import { OverlaySize } from "@shared/interfaces";
import { StationIssuesListContainer } from "@containers/Station/containers/StationIssuesListContainer";
import { SensorDataRestoringRequestCreateContainer } from "@containers/SensorDataRestoringRequest/containers/SensorDataRestoringRequestCreateContainer";
import { StationViewRawDataContainer } from "@containers/Station/containers/StationViewRawDataContainer";

import "./index.scss";

function OverlayWrapper() {
  const dispatch = useDispatch();

  const overlay = useSelector(selectors.getOverlay());

  const hideOverlayAction = useCallback(() => {
    dispatch(actions.hideOverlay());
    if (overlay?.props.onClose) {
      overlay.props.onClose();
    }
  }, [dispatch, overlay]);

  const onCloseOverlay = useCallback(() => {
    if (overlay?.leaveConfirm) {
      dispatch(
        actions.showModal({
          type: MODAL_TYPES.CONFIRM,
          props: {
            heading: "You have unsaved changes",
            content: "Are you sure you want to leave? If you leave this page now your progress will be lost.",
            cancelBtnText: "Cancel",
            successBtnText: "Leave",
            onSuccess: hideOverlayAction,
          },
        }),
      );
      return;
    }

    hideOverlayAction();
  }, [overlay, dispatch, hideOverlayAction]);

  const Component = useMemo(() => {
    if (!overlay) return;

    return overlayStrategy(overlay.type);
  }, [overlay]);

  return overlay ? (
    <>
      <div className="overlay" onClick={() => (overlay.closeOnOverlayClick ? onCloseOverlay() : null)} />
      <div className={classnames("overlay-wrapper", `overlay-wrapper_size-${overlay.size || OverlaySize.NORMAL}`)}>
        <div className="inner-wrapper">
          {Component ? <Component {...overlay.props} onClose={onCloseOverlay} /> : null}
        </div>
      </div>
    </>
  ) : null;
}

export default OverlayWrapper;

const overlayStrategy = (type: string) => {
  switch (type) {
    case OVERLAY_TYPES.CREATE_EDIT_USER: {
      return UserCreateEditContainer;
    }
    case OVERLAY_TYPES.CREATE_EDIT_STATION: {
      return StationCreateEditContainer;
    }
    case OVERLAY_TYPES.CREATE_EDIT_SENSOR: {
      return SensorCreateEditContainer;
    }
    case OVERLAY_TYPES.CREATE_EDIT_PATIENT: {
      return PatientCreateEditContainer;
    }
    case OVERLAY_TYPES.CREATE_SENSOR_DATA_FILE_FORM: {
      return SensorDataFileCreateContainer;
    }
    case OVERLAY_TYPES.CREATE_SENSOR_DATA_SESSION_FORM: {
      return SensorDataSessionCreateContainer;
    }
    case OVERLAY_TYPES.CREATE_EDIT_ALERT: {
      return AlertCreateEditContainer;
    }
    case OVERLAY_TYPES.ALERT_NOTIFICATIONS_LIST: {
      return AlertNotificationsListContainer;
    }
    case OVERLAY_TYPES.STATION_ISSUES_LIST: {
      return StationIssuesListContainer;
    }
    case OVERLAY_TYPES.CREATE_SENSOR_DATA_RESTORING_REQUEST_FORM: {
      return SensorDataRestoringRequestCreateContainer;
    }
    case OVERLAY_TYPES.VIEW_STATION_RAW_DATA: {
      return StationViewRawDataContainer;
    }
  }
};
